import { useCallback, useEffect, useRef } from 'react';

import { v4 as uuid } from 'uuid';

import track from '@/utils/pixels/helloWork';

export enum OnboardingAppFlowStepName {
  Start = 'start',
  Gender = 'gender',
  FirstName = 'first-name',
  LastName = 'last-name',
  BirthDate = 'birth-date',
  School = 'school',
  ActualStudyLevel = 'actual-study-level',
  Diploma = 'diploma',
  DiplomaYear = 'diploma-year',
  Email = 'email',
  Password = 'password',
  PasswordConfirmation = 'password-confirmation',
  ValidateConditions = 'validate-conditions',
  End = 'end',
}

export enum OnboardingEventFlowStepName {
  Start = 'start',
  EventCriteria = 'event-criterias',
  AddCv = 'add-cv',
  ValidateCv = 'validate-cv',
  AddCvLater = 'add-cv-later',
  PhoneNumber = 'phone-number',
  ValidateApplication = 'validate-application',
  AttendingMode = 'attending-mode',
  MatchingCriterias = 'matching-criterias',
}

export type OnboardingFlowStepName = OnboardingAppFlowStepName | OnboardingEventFlowStepName;
export enum OnboardingFlowName {
  App = 'onboarding-app',
  Event = 'onboading-event',
}

type OnboardingTrackEvent = {
  event: 'generic';
  event_name: 'flow';
  flow_id: string;
  flow_name: OnboardingFlowName;
  flow_step_name: OnboardingFlowStepName;
  flow_step: number;
  flow_origin: string;
};

export type TrackingInfo = {
  origin: string;
  flowName: OnboardingFlowName;
};

class OnboardingTracker {
  constructor() {
    this.init();
  }

  setOrigin(origin: string) {
    localStorage.setItem('ob-origin', origin);
  }

  setFlowName(name: OnboardingFlowName) {
    localStorage.setItem('ob-flowname', name);
  }

  setId(id: string) {
    this.id = id;
  }

  get flowName(): OnboardingFlowName {
    return localStorage.getItem('ob-flowname') as OnboardingFlowName;
  }

  get origin() {
    return localStorage.getItem('ob-origin');
  }

  trackStep({ name }: { name: OnboardingFlowStepName }) {
    this.next();
    const event: OnboardingTrackEvent = {
      event: 'generic',
      event_name: 'flow',
      flow_id: this.id,
      flow_name: this.flowName,
      flow_step_name: name,
      flow_step: this.step,
      flow_origin: this.origin,
    };

    track('flow', event);
  }

  next() {
    this.step = this.step + 1;
  }

  init(info: TrackingInfo | undefined = undefined) {
    this.id = uuid();
    this.step = 0;
    if (info) {
      this.setOrigin(info.origin);
      this.setFlowName(info.flowName);
    }
  }

  get step(): number {
    return parseInt(localStorage.getItem('ob-step') ?? '0');
  }

  set step(value: number) {
    localStorage.setItem('ob-step', value.toString());
  }

  get id() {
    return localStorage.getItem('ob-id');
  }

  set id(value: string) {
    localStorage.setItem('ob-id', value);
  }

  clean() {
    localStorage.removeItem('ob-id');
    localStorage.removeItem('ob-step');
  }
}

export function useOnboardingTracker(): { tracker: OnboardingTracker; removeTracker: () => void } {
  const trackerRef = useRef<OnboardingTracker>(undefined);

  useEffect(() => {
    if (!trackerRef.current) {
      trackerRef.current = new OnboardingTracker();
    }
    const params = new URLSearchParams(window.location.search);

    const id = params.get('flowId');
    const flowName = params.get('flowName') as OnboardingFlowName | 'null';
    let name = flowName as OnboardingFlowName;
    if (flowName && flowName === 'null') {
      name = OnboardingFlowName.App;
    }
    const flowOrigin = params.get('origin');
    let origin = flowOrigin;
    if (flowOrigin && flowOrigin === 'null') {
      origin = 'login-form';
    }
    // to avoid string 'null' value
    const step = params.get('step');

    if (flowName && flowOrigin) {
      trackerRef.current.init({
        flowName: name,
        origin: origin,
      });

      if (step && !isNaN(Number(step))) {
        trackerRef.current.step = Number(step);
      }
      if (id && id != 'null') {
        trackerRef.current.setId(id);
      }
    }
  }, []);

  const removeTracker = useCallback(() => {
    trackerRef.current?.clean();
    trackerRef.current = undefined;
  }, []);

  return {
    tracker: trackerRef.current,
    removeTracker,
  };
}
